import {
  IAnyNode,
  IEntityNode,
  IPanelColumn,
  IRootNode,
  ISearchTagsState,
  ISelectableNode,
  ISelectedEntity,
  PanelColumnType,
} from './types';
import { useCallback, useEffect, useMemo } from 'react';
import { useImmer } from 'use-immer';
import { isNil } from 'lodash';
import {
  areNodesEqual,
  isBranchNode,
  isInlineBranchNode,
  isNodeEqualToEntity,
  isNoDetailsNode,
  isSelectableNode,
} from './utils';
import { MaybeNil } from '../../../utils/interfaces';
import { Draft, freeze } from '@reduxjs/toolkit';
import { assertCondition } from '../../../utils/types';

const flattenInlineBranches = (list: IAnyNode[]): IAnyNode[] => {
  return list.flatMap((node) => {
    if (isInlineBranchNode(node)) {
      return [node, ...node.children];
    } else {
      return [node];
    }
  });
};

// find a node in the list of children that is the same as the node at pathIndex in the selectedPath
// it is important to return the node from the children (not selectedPath[pathIndex]) because it may have different
// children
const findSelectedChild = (
  selectedPath: ISelectableNode[],
  pathIndex: number,
  children: IAnyNode[],
): ISelectableNode | null => {
  if (pathIndex < selectedPath.length) {
    const selectedNode = selectedPath[pathIndex];
    const existingChild = children.find((child) =>
      areNodesEqual(child, selectedNode),
    );
    if (existingChild && isSelectableNode(existingChild)) {
      return existingChild;
    }
  }
  return null;
};

/**
 * Changes to the selected path or swapping the tree with differnt one can make the current selectedPath invalid.
 * For example, the node at index 1 (depth 2) may no longer be available because it is removed due to user search.
 * If the specific node in the path is no longer valid, a suitable replacement is picked (usually the first
 * non-presentation node) and the tree is expanded until it reaches a leaf. This way we always have a fully expanded
 * widget
 *
 * @param draft - current panel state, safe to modify
 *
 * @see IPanelStackState
 */
const updateSelectedPath = (draft: Draft<IPanelStackState>) => {
  let children = flattenInlineBranches(draft.root.children);
  const newPath: ISelectableNode[] = [];
  let pathIndex = 0;
  while (true) {
    let selected = findSelectedChild(draft.selectedPath, pathIndex, children);
    if (selected === null && children.length > 0) {
      // the node is no longer valid, find replacement
      selected = children.find(isSelectableNode) ?? null;
    }
    // no replacement - stop iteration
    if (selected === null) {
      break;
    }
    // the node is valid, let it be the path
    newPath.push(selected);

    // iterate until we reach a leaf node (i.e. non-Branch node)
    if (isBranchNode(selected)) {
      pathIndex++;
      children = flattenInlineBranches(selected.children);
    } else {
      break;
    }
  }
  draft.selectedPath = newPath;
};

// Pick a default path if the path is empty
const updateDefaultSelectedPath = (
  draft: Draft<IPanelStackState>,
  selectedEntities: ISelectedEntity[],
  defaultNode: MaybeNil<IEntityNode>,
) => {
  if (draft.selectedPath.length !== 0) {
    return;
  }

  let defaultPath: ISelectableNode[] | null = null;

  // try last selected entity first
  if (selectedEntities.length !== 0) {
    const lastEntity = selectedEntities[selectedEntities.length - 1];
    defaultPath = findSelectedEntityInTree(draft.root.children, lastEntity);
    if (
      !isNil(defaultPath) &&
      defaultPath.length > 0 &&
      isNoDetailsNode(defaultPath[0])
    ) {
      defaultPath = null;
    }
  }

  // try the Kobol supplied default
  if (isNil(defaultPath) && !isNil(defaultNode)) {
    defaultPath = findAnyNodeInTree(draft.root.children, defaultNode);
  }

  if (!isNil(defaultPath)) {
    draft.selectedPath = defaultPath;
  }
};

type INodePredicateFunction = (node: IAnyNode) => boolean;

const equalsWithAnyNode =
  (node: IAnyNode): INodePredicateFunction =>
  (other: IAnyNode) =>
    areNodesEqual(node, other);

const equalsWithSelectedEntity =
  (entity: ISelectedEntity): INodePredicateFunction =>
  (other: IAnyNode) =>
    isNodeEqualToEntity(other, entity);

const findAnyNodeInTree = (children: IAnyNode[], needle: IAnyNode) =>
  findNodeInTree(children, equalsWithAnyNode(needle), []);

const findSelectedEntityInTree = (
  children: IAnyNode[],
  needle: ISelectedEntity,
) => findNodeInTree(children, equalsWithSelectedEntity(needle), []);

// recursively finds a node in the node tree
const findNodeInTree = (
  children: IAnyNode[],
  predicate: INodePredicateFunction,
  path: ISelectableNode[],
): ISelectableNode[] | null => {
  for (const child of flattenInlineBranches(children)) {
    if (isSelectableNode(child)) {
      const newPath = [...path, child];
      if (predicate(child)) {
        return newPath;
      } else if (isBranchNode(child)) {
        const foundPath = findNodeInTree(child.children, predicate, newPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }
  }
  return null;
};

const curryOnSelectNode = (
  pathIndex: number,
  onNodeClick: (pathIndex: number, node: ISelectableNode) => void,
) => {
  return (node: ISelectableNode) => onNodeClick(pathIndex, node);
};

interface IPanelStackState {
  // root of tree
  root: IRootNode;
  // selected nodes in that tree. The depth of each node is equal to the index of the node.
  // for example, node at index 0 in selectedPath is a child of root node (depth 1), node at index 1 is a child
  // of the node at depth 2, etc.
  // Visually, each node in the selectedPath represents is the selected item in a Panel Column (from left to right)
  selectedPath: ISelectableNode[];
}

const defaultState: IPanelStackState = freeze({
  root: { children: [] },
  selectedPath: [],
});

export default (
  searchTagsState: MaybeNil<ISearchTagsState>,
  selectedEntities: ISelectedEntity[],
  shouldShowDataRefresh: boolean,
) => {
  const [panelStackState, setPanelStackState] =
    useImmer<IPanelStackState>(defaultState);

  // when user clicks on any Selectable node
  const onNodeSelect = useCallback(
    (pathIndex: number, node: ISelectableNode) => {
      setPanelStackState((draft) => {
        assertCondition(pathIndex < draft.selectedPath.length);
        draft.selectedPath[pathIndex] = node;
        updateSelectedPath(draft);
      });
    },
    [setPanelStackState],
  );

  // update the state if the search result or selected entities change
  useEffect(() => {
    if (isNil(searchTagsState)) {
      setPanelStackState(defaultState);
    } else {
      setPanelStackState((draft) => {
        draft.root = searchTagsState.root;
        updateDefaultSelectedPath(
          draft,
          selectedEntities,
          searchTagsState.defaultNode,
        );
        updateSelectedPath(draft);
      });
    }
  }, [searchTagsState, selectedEntities, setPanelStackState]);

  // derive the stack of panels from the state
  const panelStack = useMemo(() => {
    const stack: IPanelColumn[] = [];
    let children = panelStackState.root.children;
    const selectedPath = panelStackState.selectedPath;
    for (let pathIndex = 0; pathIndex < selectedPath.length; pathIndex++) {
      const selected = selectedPath[pathIndex];
      stack.push({
        index: pathIndex,
        type: PanelColumnType.LIST,
        items: children,
        selected,
        onSelectNode: curryOnSelectNode(pathIndex, onNodeSelect),
        // show event data refresh only on the first panel
        shouldShowDataRefresh: pathIndex === 0 && shouldShowDataRefresh,
      });
      if (isBranchNode(selected)) {
        children = selected.children;
        if (children.length === 0) {
          stack.push({
            type: PanelColumnType.NO_RESULTS,
          });
          break;
        }
      } else {
        if (!isNoDetailsNode(selected)) {
          stack.push({
            type: PanelColumnType.DETAILS,
            node: selected,
          });
        }
      }
    }
    return stack;
  }, [
    onNodeSelect,
    panelStackState.root.children,
    panelStackState.selectedPath,
    shouldShowDataRefresh,
  ]);

  return { panelStack };
};
